import React, { useRef, useState } from 'react';
import ImageViewer from './ImageViewer';
import { gorseliIndir, dosyaAdiUret } from './imageDownload';
import { HOUSING_TYPES } from './constants';
import Plan_88 from './assets/images/selim-sultan/12.jpeg';
import Plan_109 from './assets/images/selim-sultan/13.jpeg';
import Plan_136 from './assets/images/selim-sultan/14.jpeg';

// Kat planı görselleri konut tipi koduna göre eşleştirilir
const PLAN_GORSELLERI = {
  TIP1: Plan_136,
  TIP2: Plan_109,
  TIP3: Plan_88
};

const PLANLAR = HOUSING_TYPES
  .filter((tip) => PLAN_GORSELLERI[tip.kod])
  .map((tip) => {
    const label = `${tip.baslik} · ${tip.konsept} · ${tip.metrekare} Kat Planı`;

    return {
      kod: tip.kod,
      baslik: tip.baslik,
      konsept: tip.konsept,
      metrekare: tip.metrekare,
      src: PLAN_GORSELLERI[tip.kod],
      label,
      dosyaAdi: `${dosyaAdiUret(`Selim Sultan Konakları ${label}`)}.jpeg`
    };
  });

const SWIPE_ESIGI = 40;

/** Konut tipine göre sabit (otomatik kaymayan), yakınlaştırılabilir ve indirilebilir kat planları */
const FloorPlans = () => {
  const [aktif, setAktif] = useState(0);
  const [viewerAcik, setViewerAcik] = useState(false);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const dokunusRef = useRef(null);
  const swipeZamaniRef = useRef(0);

  if (!PLANLAR.length) return null;

  const plan = PLANLAR[aktif];

  const onTouchStart = (event) => {
    const dokunus = event.touches[0];
    dokunusRef.current = { x: dokunus.clientX, y: dokunus.clientY };
  };

  // Plan görselinde sağa/sola kaydırarak konut tipi değiştirilir
  const onTouchEnd = (event) => {
    const baslangic = dokunusRef.current;
    dokunusRef.current = null;
    if (!baslangic || PLANLAR.length < 2) return;

    const dokunus = event.changedTouches[0];
    const dx = dokunus.clientX - baslangic.x;
    const dy = dokunus.clientY - baslangic.y;
    if (Math.abs(dx) < SWIPE_ESIGI || Math.abs(dx) <= Math.abs(dy)) return;

    swipeZamaniRef.current = Date.now();
    setAktif((prev) => (prev + (dx < 0 ? 1 : -1) + PLANLAR.length) % PLANLAR.length);
  };

  // Kaydırma sonrası gelen tıklama görüntüleyiciyi açmasın
  const onPlanClick = () => {
    if (Date.now() - swipeZamaniRef.current < 400) return;
    setViewerAcik(true);
  };

  const onIndir = async () => {
    setIndiriliyor(true);
    try {
      await gorseliIndir(plan.src, plan.dosyaAdi, plan.label);
    } finally {
      setIndiriliyor(false);
    }
  };

  return (
    <div className="mt-8">
      <h3 className="text-lg font-bold text-gray-900 mb-1">Kat Planları</h3>
      <p className="text-sm text-gray-600 mb-4">
        Konut tipini seçin veya plan üzerinde sağa/sola kaydırın. Plana dokunarak yakınlaştırabilir, cihazınıza indirebilirsiniz.
      </p>

      {/* Konut tipi sekmeleri */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {PLANLAR.map((item, sira) => {
          const secili = sira === aktif;

          return (
            <button
              key={item.kod}
              type="button"
              onClick={() => setAktif(sira)}
              className={`
                shrink-0 rounded-xl border px-4 py-2.5 text-center transition-colors
                ${secili
                  ? 'bg-primary border-primary text-white shadow-sm'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-primary/40'
                }
              `}
            >
              <span className="block text-sm font-bold leading-none">{item.metrekare}</span>
              <span className={`block text-[11px] mt-1 ${secili ? 'text-white/90' : 'text-gray-500'}`}>
                {item.baslik} · {item.konsept}
              </span>
            </button>
          );
        })}
      </div>

      {/* Sabit plan görseli — kırpılmadan, tam olarak gösterilir */}
      <button
        type="button"
        onClick={onPlanClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-label={`${plan.label} görselini büyüt`}
        className="relative mt-3 block w-full overflow-hidden rounded-xl border-2 border-primary/10 bg-white shadow-sm active:scale-[0.99] transition-transform"
      >
        <img
          src={plan.src}
          alt={plan.label}
          className="w-full h-auto object-contain"
        />
        <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-[11px] font-medium text-white backdrop-blur-sm">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 8v6m-3-3h6m5 0a8 8 0 11-16 0 8 8 0 0116 0z" />
          </svg>
          Büyütmek için dokunun
        </span>
      </button>

      {/* İndirme */}
      <button
        type="button"
        onClick={onIndir}
        disabled={indiriliyor}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary-light px-6 py-3 font-semibold text-primary transition-colors hover:bg-primary/10 active:scale-[0.98] disabled:opacity-60"
      >
        {indiriliyor ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
              <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            İndiriliyor…
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 19h16" />
            </svg>
            Kat Planını İndir
          </>
        )}
      </button>

      {viewerAcik && (
        <ImageViewer
          images={PLANLAR}
          startIndex={aktif}
          onClose={() => setViewerAcik(false)}
        />
      )}
    </div>
  );
};

export default FloorPlans;
